import { useState } from 'react';
import { AlertTriangleIcon, Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDeleteHubStatusesBefore } from '@/services/hub.service';
import { parseUtcInput, utcInputValue, utcStamp } from './fieldTime';

/**
 * Trim a hub's reported status history down to a cutoff. Destructive and not
 * undoable, so the date is picked first and confirmed on a second screen before
 * anything reaches the backend.
 */
export default function HubStatusCleanup({
    macAddress,
    onClose,
}: {
    macAddress: string;
    onClose: () => void;
}) {
    // Wall time as typed into the picker (`YYYY-MM-DDTHH:mm`), read as UTC:
    // the rest of the Field pages is UTC, and a cutoff that meant something
    // else depending on who was looking would delete a different history here
    // than the grid it was picked off shows.
    const [before, setBefore] = useState('');
    const [confirming, setConfirming] = useState(false);
    const cleanUp = useDeleteHubStatusesBefore();

    const cutoff = parseUtcInput(before);
    const cutoffLabel = cutoff ? utcStamp(cutoff) : '';

    if (cleanUp.isSuccess) {
        return (
            <div className="flex flex-col gap-4">
                <p className="text-sm text-[#d8dde2]">
                    Deleted{' '}
                    <span className="font-semibold text-white tabular-nums">
                        {cleanUp.data.data.deleted}
                    </span>{' '}
                    status {cleanUp.data.data.deleted === 1 ? 'entry' : 'entries'}{' '}
                    recorded before {cutoffLabel}.
                </p>
                <div className="flex justify-end">
                    <Button
                        size="sm"
                        onClick={onClose}
                        className="bg-[#0f141a] border border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    if (confirming) {
        return (
            <div className="flex flex-col gap-4">
                <div className="flex gap-3 items-start">
                    <AlertTriangleIcon className="h-5 w-5 text-[#e0b341] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[#d8dde2]">
                        <p>
                            Permanently delete every status hub{' '}
                            <span className="font-mono text-white">
                                {macAddress}
                            </span>{' '}
                            reported before{' '}
                            <span className="font-semibold text-white">
                                {cutoffLabel}
                            </span>
                            ?
                        </p>
                        <p className="text-[#6b7480] mt-2">
                            This cannot be undone. Those wake-ups will also drop
                            off the dot grid on the Field page.
                        </p>
                    </div>
                </div>
                {cleanUp.isError && (
                    <p className="text-sm text-red-400">
                        Delete failed. Nothing was removed.
                    </p>
                )}
                <div className="flex justify-end gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={cleanUp.isPending}
                        onClick={() => setConfirming(false)}
                        className="bg-transparent border-[#1e242a] text-[#a8b0bb] hover:bg-[#1a2126] hover:text-white"
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        disabled={cleanUp.isPending || !cutoff}
                        onClick={() =>
                            cutoff &&
                            cleanUp.mutate({
                                // A real instant: the picker gives bare wall
                                // time, which the server must not re-read in
                                // its own timezone.
                                macAddress,
                                before: cutoff.toISOString(),
                            })
                        }
                        className="bg-[#d9534f] text-white hover:bg-[#c9302c]"
                    >
                        {cleanUp.isPending && (
                            <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        )}
                        Delete permanently
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
                <label
                    htmlFor="cleanup-before"
                    className="text-xs text-[#a8b0bb]"
                >
                    Delete statuses recorded before (UTC)
                </label>
                <input
                    id="cleanup-before"
                    type="datetime-local"
                    value={before}
                    max={utcInputValue(new Date())}
                    onChange={(event) => setBefore(event.target.value)}
                    className="px-2 py-1.5 rounded text-sm bg-[#0f141a] border border-[#2d2d2d] text-[#d8dde2] focus:outline-none focus:border-[#74c69d] [color-scheme:dark]"
                />
                <p className="text-[11px] text-[#6b7480]">
                    UTC, like everything else on this page. Everything from that
                    moment onwards is kept.
                </p>
            </div>
            <div className="flex justify-end">
                <Button
                    size="sm"
                    disabled={!cutoff}
                    onClick={() => setConfirming(true)}
                    className="bg-[#0f141a] border border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                >
                    Continue
                </Button>
            </div>
        </div>
    );
}
